import { useCallback } from "react";
import type { Source } from "@/components/api/fiinder-api-types";
import { usePersonation } from "@/lib/personation-context";
import { usePdfViewer } from "@/components/ui/pdf-viewer/pdf-context";
import { navigateToUrl as windowNavigateToUrl } from "@/lib/window-utils";
import { applyLinkRegexReplaces, isPdfUrl, pageNumberFromPdfUrl, stripPageParam } from "@/lib/url-utils";
import { addRecentPage } from "@/lib/recent-pages-storage";

type NavigateToSourceParams = {
  source: Source;
};

type NavigateToUrlParams = {
  url: string;
  fetchUrl?: string;
  title?: string;
  description?: string;
  keywords?: string[];
  og_image?: string;
};

type UseSourceNavigationReturn = {
  navigateToSource: (params: NavigateToSourceParams) => void;
  navigateToUrl: (params: NavigateToUrlParams) => void;
};

function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * Hook that centralizes PDF-aware navigation logic.
 *
 * "Our viewer can render this inline" is signalled by `fetchUrl` presence
 * AND its own filename ending in `.pdf` — never the public url's suffix
 * (unreliable: a redirector, a CMS page), never a `type_of_doc` flag. Our
 * `/static` proxy exists for any Cruddy-internal file, not just pdfs, so
 * presence alone is not enough. Its absence, or a non-pdf fetchUrl, means
 * the document is opened like any other link, never a dead/wrong-viewer
 * attempt.
 */
export function useSourceNavigation(): UseSourceNavigationReturn {
  const { linkTarget, linkRegexReplaces } = usePersonation();
  const { openPdf } = usePdfViewer();

  const navigateToUrl = useCallback(
    ({ url, fetchUrl, title, description, keywords, og_image }: NavigateToUrlParams) => {
      const transformedUrl = applyLinkRegexReplaces(url, linkRegexReplaces);
      if (!isValidUrl(transformedUrl)) return;

      addRecentPage({
        title: title ?? "",
        url: transformedUrl,
        fetchUrl,
        description,
        keywords,
        og_image,
      });

      if (fetchUrl && isValidUrl(fetchUrl) && isPdfUrl(fetchUrl)) {
        // The `page` param is our own deep-link marker, stamped by the api
        // onto fetch_url only (never the public url). It is stripped before
        // the fetch — the authenticated proxy doesn't know it.
        const pageNumber = pageNumberFromPdfUrl(fetchUrl) ?? undefined;
        openPdf({ staticUrl: stripPageParam(fetchUrl), title: title ?? "", pageNumber });
        return;
      }
      windowNavigateToUrl(transformedUrl, linkTarget);
    },
    [linkTarget, linkRegexReplaces, openPdf]
  );

  const navigateToSource = useCallback(
    ({ source }: NavigateToSourceParams) => {
      navigateToUrl({
        url: source.url,
        fetchUrl: source.fetch_url ?? undefined,
        title: source.title ?? undefined,
        description: source.description ?? undefined,
        keywords: source.keywords ?? undefined,
        og_image: source.og_image ?? undefined,
      });
    },
    [navigateToUrl]
  );

  return { navigateToSource, navigateToUrl };
}
